import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import MfaPasswordUnlock from './MfaPasswordUnlock';
import { Download, Upload, RefreshCw, ChevronDown, ChevronUp, Shield, ShieldCheck, ShieldAlert, Key, Mail, Smartphone, Users, Building2, LogIn, Pencil, Check, X, Lock } from 'lucide-react';

// Simplified User Groups - Branch-based with dropdowns per group
const UserGroupsManager = ({ allUsers, onUpdate }) => {
  const [users, setUsers] = useState(allUsers || []);
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    setUsers(allUsers);
  }, [allUsers]);

  const availableGroups = [
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'operations', label: 'Operations' },
    { value: 'sales_office', label: 'Sales Office' },
    { value: 'admin', label: 'Admin' },
    { value: 'all', label: 'All' }
  ];

  const branches = [
    { id: 'E70', name: 'E70 West Brom' },
    { id: 'C30', name: 'C30 Birmingham' }
  ];

  const isUserInGroup = (userId, branchId, groupValue) => {
    const user = users.find(u => u.id === userId);
    if (!user) return false;
    const assignment = user.group_assignments?.find(a => a.branch_id === branchId);
    return assignment?.groups?.includes(groupValue) || false;
  };

  const toggleUserInGroup = async (userId, branchId, groupValue) => {
    setSaving(true);
    try {
      const user = users.find(u => u.id === userId);
      const assignment = user.group_assignments?.find(a => a.branch_id === branchId);
      const currentGroups = assignment?.groups || [];
      
      const newGroups = currentGroups.includes(groupValue)
        ? currentGroups.filter(g => g !== groupValue)
        : [...currentGroups, groupValue];

      await api.put(`${API}/users/${userId}/groups`, {
        branch_id: branchId,
        groups: newGroups
      });

      // Update local state
      setUsers(prevUsers => prevUsers.map(u => {
        if (u.id !== userId) return u;
        
        const updatedAssignments = [...(u.group_assignments || [])];
        const existingIndex = updatedAssignments.findIndex(a => a.branch_id === branchId);
        
        if (existingIndex >= 0) {
          updatedAssignments[existingIndex].groups = newGroups;
        } else {
          updatedAssignments.push({ branch_id: branchId, groups: newGroups });
        }
        
        return { ...u, group_assignments: updatedAssignments };
      }));

      toast.success('User group updated');
      onUpdate();
    } catch (error) {
      console.error('Error updating user group:', error);
      toast.error('Failed to update user group');
    } finally {
      setSaving(false);
    }
  };

  const getUsersInGroup = (branchId, groupValue) => {
    return users.filter(u => isUserInGroup(u.id, branchId, groupValue));
  };

  return (
    <div className="space-y-6">
      {branches.map((branch) => (
        <Card key={branch.id} className="border-blue-200">
          <CardHeader className="bg-blue-50/50">
            <CardTitle className="text-lg text-blue-900 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {branch.name}
            </CardTitle>
            <CardDescription>
              Assign users to business unit groups for this branch
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {availableGroups.map((group) => {
                const groupKey = `${branch.id}-${group.value}`;
                const isExpanded = expandedGroups[groupKey];
                const usersInGroup = getUsersInGroup(branch.id, group.value);

                return (
                  <div key={group.value} className="border rounded-lg overflow-hidden">
                    {/* Group Header - Clickable */}
                    <button
                      onClick={() => setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                      className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-3">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">{group.label}</span>
                        <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                          {usersInGroup.length} user{usersInGroup.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <ChevronDown 
                        className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {/* User List - Expandable */}
                    {isExpanded && (
                      <div className="p-3 bg-white border-t max-h-64 overflow-y-auto">
                        {users.filter(u => u.role !== 'super_admin').length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-2">No users available</p>
                        ) : (
                          <div className="space-y-2">
                            {users
                              .filter(u => u.role !== 'super_admin')
                              .map(user => {
                                const isInGroup = isUserInGroup(user.id, branch.id, group.value);
                                
                                return (
                                  <div
                                    key={user.id}
                                    className={`flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
                                      isInGroup ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 hover:bg-gray-100'
                                    }`}
                                  >
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">{user.full_name || user.username}</span>
                                      </div>
                                      <span className="text-xs text-gray-500">{user.email}</span>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isInGroup}
                                        onChange={() => toggleUserInGroup(user.id, branch.id, group.value)}
                                        disabled={saving}
                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                      />
                                    </label>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export const PermissionsManager = ({ onImpersonate, view = 'users' }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  // User Admin has two sub-headers: Users (the users and their permissions, their business unit groups, and the permissions download / upload) and Pending Accounts.
  const [section, setSection] = useState('users');
  const [loading, setLoading] = useState(true);
  const [showPasswordManagerModal, setShowPasswordManagerModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState({});
  // Inline username editing
  const [editingUsernameId, setEditingUsernameId] = useState(null);
  const [usernameInput, setUsernameInput] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);
  
  // MFA State
  const [mfaSettings, setMfaSettings] = useState({
    mfa_required_globally: false,
    authenticator_enabled: true,
    total_users: 0,
    mfa_enabled_count: 0,
    users: []
  });
  const [loadingMfa, setLoadingMfa] = useState(false);
  const [showMfaUsersModal, setShowMfaUsersModal] = useState(false);
  const [mfaModalFilter, setMfaModalFilter] = useState('enabled'); // 'enabled' or 'disabled'
  // Protected MFA settings: single-use token issued after the super admin re-enters their own password
  const [stepUpToken, setStepUpToken] = useState(null);
  const [showUnlock, setShowUnlock] = useState(false);
  const [pendingMfaOff, setPendingMfaOff] = useState(false);

  useEffect(() => {
    loadUsers();
    loadPendingUsers();
    loadMfaSettings();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingUsers = async () => {
    try {
      const response = await api.get(`${API}/users/pending`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPendingUsers(response.data);
    } catch (error) {
      console.error('Error loading pending users:', error);
    }
  };
  
  const loadMfaSettings = async () => {
    setLoadingMfa(true);
    try {
      const response = await api.get(`${API}/admin/mfa/settings`);
      setMfaSettings(response.data);
    } catch (error) {
      console.error('Error loading MFA settings:', error);
    } finally {
      setLoadingMfa(false);
    }
  };
  
  const applyMfaRequired = async (newValue, token) => {
    try {
      await api.post(`${API}/admin/mfa/settings`, { mfa_required: newValue, step_up_token: token || undefined });
      setMfaSettings(prev => ({ ...prev, mfa_required_globally: newValue }));
      setStepUpToken(null);
      toast.success(newValue ? 'MFA is now required for all users' : 'MFA requirement disabled');
    } catch (error) {
      console.error('Error updating MFA settings:', error);
      setStepUpToken(null);
      toast.error(error?.response?.data?.detail || 'Failed to update MFA settings');
    }
  };

  const toggleMfaRequired = async () => {
    const newValue = !mfaSettings.mfa_required_globally;
    if (!newValue && !stepUpToken) {
      setPendingMfaOff(true);
      setShowUnlock(true);
      return;
    }
    await applyMfaRequired(newValue, stepUpToken);
  };

  const handleUnlocked = async (token) => {
    if (pendingMfaOff) {
      setPendingMfaOff(false);
      await applyMfaRequired(false, token);
      return;
    }
    setStepUpToken(token);
    toast.success('Unlocked for 5 minutes. The authenticator setting can now be changed once.');
  };
  
  const toggleAuthenticatorEnabled = async () => {
    try {
      const newValue = !mfaSettings.authenticator_enabled;
      await api.post(`${API}/admin/mfa/settings`, { authenticator_enabled: newValue, step_up_token: stepUpToken });
      toast.success(newValue ? 'Authenticator app MFA re-enabled' : 'Authenticator app disabled — email MFA is now mandatory for everyone');
      setStepUpToken(null);
      loadMfaSettings();
    } catch (error) {
      console.error('Error updating authenticator setting:', error);
      setStepUpToken(null);
      toast.error(error?.response?.data?.detail || 'Failed to update authenticator setting');
    }
  };
  
  const resetUserMfa = async (userId, userName) => {
    if (!window.confirm(`Reset MFA for ${userName}? They will need to set up MFA again.`)) {
      return;
    }
    try {
      await api.post(`${API}/admin/mfa/reset-user/${userId}`);
      toast.success(`MFA reset for ${userName}`);
      loadMfaSettings();
    } catch (error) {
      console.error('Error resetting MFA:', error);
      toast.error('Failed to reset MFA');
    }
  };
  
  const toggleUserExpanded = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const approveUser = async (userId) => {
    try {
      await api.post(`${API}/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('User approved! They can now login and you can set their page permissions below.');
      loadPendingUsers();
      loadUsers();
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error('Failed to approve user');
    }
  };

  const rejectUser = async (userId) => {
    try {
      await api.delete(`${API}/users/${userId}/decline`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('User request rejected');
      loadPendingUsers();
    } catch (error) {
      console.error('Error rejecting user:', error);
      toast.error('Failed to reject user');
    }
  };

  const saveUsername = async (userId) => {
    const trimmed = usernameInput.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) return toast.error('Username must be a valid email address');
    setSavingUsername(true);
    try {
      await api.put(`${API}/users/${userId}/permissions`, { username: trimmed });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, username: trimmed } : u));
      toast.success(`Username updated to ${trimmed}. Team management will now use this address.`);
      setEditingUsernameId(null);
      setUsernameInput('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update username');
    } finally {
      setSavingUsername(false);
    }
  };

  const togglePermission = async (userId, permissionKey) => {
    const user = users.find(u => u.id === userId);
    const newValue = !user[permissionKey];

    // Optimistic update: flip the switch immediately so the controlled Switch
    // component reflects the new state without waiting for the API round-trip.
    // Without this, the controlled Switch reverts to its previous value for the
    // duration of the network request, making it look like the save failed.
    setUsers(prev => prev.map(u =>
      u.id === userId ? { ...u, [permissionKey]: newValue } : u
    ));

    try {
      await api.put(`${API}/users/${userId}/permissions`, {
        [permissionKey]: newValue
      });
      toast.success('Permission updated');
    } catch (error) {
      // Rollback the optimistic update on failure
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, [permissionKey]: !newValue } : u
      ));
      console.error('Error updating permission:', error);
      toast.error('Failed to update permission');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`${API}/users/${userId}`);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const resetUserPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }

    try {
      await api.post(`${API}/users/${selectedUserForPassword.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Password reset for ${selectedUserForPassword.username}`);
      setShowPasswordManagerModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setSelectedUserForPassword(null);
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error?.response?.data?.detail || 'Failed to reset password');
    }
  };

  const downloadPermissions = () => {
    try {
      const permissionsData = users.map(user => ({
        email: user.email,
        full_name: user.full_name,
        role: user.role,
        permissions: {
          can_access_transport: user.can_access_transport || false,
          can_access_reports: user.can_access_reports || false,
          can_access_company_car: user.can_access_company_car || false,
          can_access_settings: user.can_access_settings || false,
          can_access_sales: user.can_access_sales || false,
          can_upload_sales_tracker: user.can_upload_sales_tracker || false,
          can_access_global_upload: user.can_access_global_upload || false,
          can_view_error_log: user.can_view_error_log || false,
          can_edit_dashboard_dropdown: user.can_edit_dashboard_dropdown || false
        }
      }));

      const dataStr = JSON.stringify(permissionsData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `permissions-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Downloaded permissions for ${users.length} users`);
    } catch (error) {
      console.error('Error downloading permissions:', error);
      toast.error('Failed to download permissions');
    }
  };

  const uploadPermissions = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const permissionsData = JSON.parse(text);

      if (!Array.isArray(permissionsData)) {
        toast.error('Invalid permissions file format');
        return;
      }

      toast.info('Uploading permissions...');
      
      let successCount = 0;
      let errorCount = 0;

      for (const userPermissions of permissionsData) {
        try {
          const user = users.find(u => u.email === userPermissions.email);
          if (!user) {
            console.warn(`User not found: ${userPermissions.email}`);
            errorCount++;
            continue;
          }

          await api.put(`${API}/users/${user.id}/permissions`, {
            role: userPermissions.role,
            ...userPermissions.permissions
          });
          
          successCount++;
        } catch (error) {
          console.error(`Error updating user ${userPermissions.email}:`, error);
          errorCount++;
        }
      }

      toast.success(`Uploaded permissions: ${successCount} successful, ${errorCount} failed`);
      loadUsers();
    } catch (error) {
      console.error('Error uploading permissions:', error);
      toast.error('Failed to upload permissions file');
    }

    event.target.value = '';
  };

  const runPermissionsMigration = async () => {
    if (!window.confirm('This will update ALL users with the latest default permissions:\n\n• Customer Management: ON for everyone\n• Sales Dashboard Projects: ON for everyone\n• Sales Dashboard Budgets: OFF (super_admin only)\n\nContinue?')) {
      return;
    }

    setMigrating(true);
    try {
      const response = await api.post(`${API}/users/migrate-permissions`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success(response.data.message || 'Migration completed successfully!');
      loadUsers();
    } catch (error) {
      console.error('Error running migration:', error);
      toast.error(error.response?.data?.detail || 'Failed to run migration');
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading users...</div>;
  }

  return (
    <div className="space-y-6">
      {/* One component, three views (the view prop): 'users' is the User Admin screen with its two sub-headers (Users and Pending Accounts); 'mfa' is the MFA settings box,
          shown in Settings > MFA; 'adhoc' is the display option, shown in Settings > System > Adhoc. */}
      {view === 'users' && (
        <div className="v3-ua-subheaders flex items-center gap-2 flex-wrap" role="tablist" aria-label="User Admin sections" data-testid="user-admin-subheaders">
          <button
            type="button"
            role="tab"
            aria-selected={section === 'users'}
            onClick={() => setSection('users')}
            className={`v3-ua-sub inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border ${section === 'users' ? 'v3-ua-sub--on bg-[#ee7203] border-[#ee7203] text-white' : 'bg-white border-gray-200 text-gray-700'}`}
            data-testid="user-admin-sub-users"
          >
            Users
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={section === 'pending'}
            onClick={() => setSection('pending')}
            className={`v3-ua-sub inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border ${section === 'pending' ? 'v3-ua-sub--on bg-[#ee7203] border-[#ee7203] text-white' : 'bg-white border-gray-200 text-gray-700'}`}
            data-testid="user-admin-sub-pending"
          >
            Pending Accounts
            {pendingUsers.length > 0 && (
              <span className="v3-ua-badge inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold" data-testid="user-admin-pending-badge">
                {pendingUsers.length}
              </span>
            )}
          </button>
        </div>
      )}
      <div className="flex flex-col gap-6">
      {view === 'mfa' && (
      <div>
      {/* Multi-Factor Authentication Section */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Multi-Factor Authentication (MFA)
          </CardTitle>
          <CardDescription>
            Manage two-factor authentication settings for enhanced security
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Global MFA Requirement */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border-2 border-blue-200">
            <div className="flex items-center gap-3">
              {mfaSettings.mfa_required_globally ? (
                <ShieldCheck className="w-8 h-8 text-green-600" />
              ) : (
                <ShieldAlert className="w-8 h-8 text-yellow-600" />
              )}
              <div>
                <Label className="text-base font-semibold">Require MFA for All Users</Label>
                <p className="text-sm text-gray-500">
                  When enabled, all users must set up MFA before they can access the system
                </p>
              </div>
            </div>
            <Switch
              checked={mfaSettings.mfa_required_globally}
              onCheckedChange={toggleMfaRequired}
              className="data-[state=checked]:bg-blue-600"
            />
          </div>
          
          {/* Authenticator App Toggle */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border-2 border-blue-200">
            <div className="flex items-center gap-3">
              {mfaSettings.authenticator_enabled ? (
                <Smartphone className="w-8 h-8 text-blue-600" />
              ) : (
                <Mail className="w-8 h-8 text-blue-600" />
              )}
              <div>
                <Label className="text-base font-semibold">Allow Google Authenticator App</Label>
                <p className="text-sm text-gray-500">
                  {mfaSettings.authenticator_enabled
                    ? 'Users can choose an authenticator app or email codes for MFA.'
                    : 'Off — every user verifies by email code only. No one can log in without it, so no user is ever shown as missing MFA.'}
                </p>
              </div>
            </div>
            {stepUpToken ? (
              <Switch
                checked={mfaSettings.authenticator_enabled}
                onCheckedChange={toggleAuthenticatorEnabled}
                className="data-[state=checked]:bg-blue-600"
                data-testid="toggle-authenticator-enabled"
              />
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => { setPendingMfaOff(false); setShowUnlock(true); }}
                data-testid="unlock-authenticator-setting"
              >
                <Lock className="w-4 h-4" />
                Unlock (password)
              </Button>
            )}
          </div>
          
          {/* MFA Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-lg border text-center">
              <div className="text-2xl font-bold text-blue-600">{mfaSettings.total_users}</div>
              <div className="text-sm text-gray-500">Total Users</div>
            </div>
            <div 
              className="p-4 bg-white rounded-lg border text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-all"
              onClick={() => { setMfaModalFilter('enabled'); setShowMfaUsersModal(true); }}
            >
              <div className="text-2xl font-bold text-green-600">{mfaSettings.mfa_enabled_count}</div>
              <div className="text-sm text-gray-500">MFA Enabled</div>
              <div className="text-xs text-green-600 mt-1">Click to manage</div>
            </div>
            <div 
              className="p-4 bg-white rounded-lg border text-center cursor-pointer hover:border-red-400 hover:bg-red-50 transition-all"
              onClick={() => { setMfaModalFilter('disabled'); setShowMfaUsersModal(true); }}
            >
              <div className="text-2xl font-bold text-red-600">
                {mfaSettings.total_users - mfaSettings.mfa_enabled_count}
              </div>
              <div className="text-sm text-gray-500">Without MFA</div>
              <div className="text-xs text-red-600 mt-1">Click to view</div>
            </div>
          </div>
          
          {/* Users without MFA */}
          {mfaSettings.mfa_required_globally && mfaSettings.users.filter(u => !u.effective_mfa_enabled).length > 0 && (
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4" />
                Users Without MFA ({mfaSettings.users.filter(u => !u.effective_mfa_enabled).length})
              </h4>
              <p className="text-sm text-yellow-700 mb-3">
                These users will be prompted to set up MFA on their next login.
              </p>
              <div className="flex flex-wrap gap-2">
                {mfaSettings.users.filter(u => !u.effective_mfa_enabled).slice(0, 10).map(u => (
                  <span key={u.id} className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                    {u.full_name || u.email}
                  </span>
                ))}
                {mfaSettings.users.filter(u => !u.effective_mfa_enabled).length > 10 && (
                  <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full text-xs">
                    +{mfaSettings.users.filter(u => !u.effective_mfa_enabled).length - 10} more
                  </span>
                )}
              </div>
            </div>
          )}
          
          <Button
            variant="outline"
            onClick={loadMfaSettings}
            disabled={loadingMfa}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loadingMfa ? 'animate-spin' : ''}`} />
            Refresh MFA Status
          </Button>
        </CardContent>
      </Card>

      </div>
      )}
      {view === 'users' && section === 'users' && (
      <div style={{ order: 3 }} data-testid="user-admin-permissions-management">
      {/* Permissions Import/Export Section */}
      <Card>
        <CardHeader>
          <CardTitle>Permissions Management</CardTitle>
          <CardDescription>
            Download current permissions configuration or upload to apply settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={downloadPermissions}
              variant="outline"
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Download Permissions
            </Button>
            
            <div>
              <input
                type="file"
                accept=".json"
                onChange={uploadPermissions}
                className="hidden"
                id="upload-permissions"
              />
              <Button
                onClick={() => document.getElementById('upload-permissions').click()}
                variant="outline"
                className="gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload Permissions
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Download permissions to backup or transfer settings. Upload a previously downloaded file to restore permissions configuration.
          </p>
          
          {/* Database Migration Section */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">Database Migration</h4>
            <p className="text-sm text-gray-600 mb-3">
              Run this after deployment to apply new default permissions to all existing users.
              This will enable Customer Management access for everyone.
            </p>
            <Button
              onClick={runPermissionsMigration}
              disabled={migrating}
              className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
            >
              {migrating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Running Migration...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Run Permissions Migration
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      </div>
      )}
      {view === 'users' && section === 'pending' && (
      <div style={{ order: 1 }} data-testid="user-admin-pending">
      {/* Pending Approvals Section */}
      {pendingUsers.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-700 flex items-center gap-2">
              <span className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                {pendingUsers.length}
              </span>
              Pending Account Approvals
            </CardTitle>
            <CardDescription>
              New users waiting for your approval to access the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingUsers.map((pending) => (
                <div
                  key={pending.id}
                  className="flex items-center justify-between p-4 bg-white border border-orange-200 rounded-lg"
                >
                  <div>
                    <div className="font-medium">{pending.full_name}</div>
                    <div className="text-sm text-gray-500">{pending.email}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Requested: {new Date(pending.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => approveUser(pending.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => rejectUser(pending.id)}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {pendingUsers.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>No pending accounts</CardTitle>
            <CardDescription>When someone asks for an account, it appears here for you to approve or reject.</CardDescription>
          </CardHeader>
        </Card>
      )}
      </div>
      )}
      {view === 'adhoc' && (
      <div data-testid="adhoc-display-settings">
      {/* Show Sales Values Per Line Setting */}
      <Card>
        <CardHeader>
          <CardTitle>Display Settings</CardTitle>
          <CardDescription>
            Control what information is displayed to users
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
            <div>
              <Label className="text-base font-semibold">Show Sales Values Per Line</Label>
              <p className="text-sm text-gray-500 mt-1">
                When enabled, sales order values will be displayed for each delivery entry
              </p>
            </div>
            <Button
              onClick={async () => {
                try {
                  const response = await api.get(`${API}/settings`);
                  const currentSettings = response.data;
                  const newValue = !currentSettings.show_sales_per_line;
                  
                  await api.put(`${API}/settings`, {
                    ...currentSettings,
                    show_sales_per_line: newValue
                  });
                  
                  toast.success(`Sales values ${newValue ? 'shown' : 'hidden'}`);
                  window.location.reload();
                } catch (error) {
                  console.error('Error updating sales visibility:', error);
                  toast.error('Failed to update setting');
                }
              }}
              variant="outline"
              className="gap-2"
            >
              Toggle Sales Display
            </Button>
          </div>
        </CardContent>
      </Card>
    
      </div>
      )}
      {view === 'users' && section === 'users' && (
      <div className="flex flex-col gap-6" style={{ order: 1 }} data-testid="user-admin-users">
      {/* User Groups Section - NEW */}
      <Card className="border-blue-200 bg-blue-50/30" style={{ order: 2 }}>
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            User Business Unit Groups
          </CardTitle>
          <CardDescription>
            Organize users into business unit groups (Warehouse, Operations, Sales Office, Admin).
            Groups are branch-specific and used for PI Count assignments and user filtering.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <UserGroupsManager allUsers={users} onUpdate={loadUsers} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>User Page Permissions</CardTitle>
          <CardDescription>
            Control which pages each user can access. Click on a user to expand their permissions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {users.map((user) => {
              const userMfaInfo = mfaSettings.users.find(u => u.id === user.id);
              const realMfaEnabled = userMfaInfo?.mfa_enabled || false;
              const hasMfa = userMfaInfo?.effective_mfa_enabled ?? realMfaEnabled;
              const isExpanded = expandedUsers[user.id] || false;
              
              return (
                <div
                  key={user.id}
                  className="border rounded-lg overflow-hidden"
                >
                  {/* User Header - Always Visible */}
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleUserExpanded(user.id)}
                  >
                    <div className="flex items-center gap-3">
                      <button className="text-gray-400 hover:text-gray-600">
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {editingUsernameId === user.id ? (
                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <Input
                                value={usernameInput}
                                onChange={e => setUsernameInput(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') saveUsername(user.id);
                                  if (e.key === 'Escape') { setEditingUsernameId(null); setUsernameInput(''); }
                                }}
                                className="h-7 text-sm w-56 px-2"
                                autoFocus
                                data-testid={`username-input-${user.id}`}
                                placeholder="email@company.co.uk"
                              />
                              <button
                                disabled={savingUsername}
                                onClick={() => saveUsername(user.id)}
                                data-testid={`username-save-${user.id}`}
                                className="w-6 h-6 flex items-center justify-center rounded bg-green-500 hover:bg-green-600 text-white transition-colors flex-shrink-0"
                              ><Check className="w-3.5 h-3.5" /></button>
                              <button
                                onClick={() => { setEditingUsernameId(null); setUsernameInput(''); }}
                                data-testid={`username-cancel-${user.id}`}
                                className="w-6 h-6 flex items-center justify-center rounded bg-gray-200 hover:bg-gray-300 text-gray-600 transition-colors flex-shrink-0"
                              ><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 group">
                              <span>{user.username}</span>
                              <button
                                onClick={e => { e.stopPropagation(); setEditingUsernameId(user.id); setUsernameInput(user.username || ''); }}
                                data-testid={`username-edit-${user.id}`}
                                title="Edit login username / email (fixes team management order attribution)"
                                className="text-gray-300 hover:text-orange-500 transition-colors"
                              ><Pencil className="w-3 h-3" /></button>
                            </div>
                          )}
                          {user.microsoft_auth && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Microsoft Auth</span>
                          )}
                          {hasMfa ? (
                            <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${realMfaEnabled ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {(!mfaSettings.authenticator_enabled || userMfaInfo?.mfa_method === 'email') ? (
                                <><Mail className="w-3 h-3" /> Email MFA{!realMfaEnabled ? ' · not logged in yet' : ''}</>
                              ) : (
                                <><Smartphone className="w-3 h-3" /> App MFA</>
                              )}
                            </span>
                          ) : mfaSettings.mfa_required_globally && (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded flex items-center gap-1">
                              <ShieldAlert className="w-3 h-3" /> No MFA
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.full_name || user.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        user.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                        user.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {user.role}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        user.approved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {user.approved ? 'Approved' : 'Pending'}
                      </span>
                      {user.team && (
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          user.team === 'warehouse' ? 'bg-amber-100 text-amber-700' :
                          user.team === 'operations' ? 'bg-teal-100 text-teal-700' :
                          user.team === 'office_sales' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {user.team === 'office_sales' ? 'Office Sales' : 
                           user.team === 'operations' ? 'Operations' :
                           user.team === 'warehouse' ? 'Warehouse' :
                           user.team === 'admin' ? 'Admin' : user.team}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Expandable Permissions Section */}
                  {isExpanded && (
                    <div className="p-4 border-t bg-gray-50 space-y-3">
                      {/* User Details Row */}
                      <div className="flex flex-wrap items-center gap-4 pb-3 border-b">
                        <div className="text-xs text-gray-400 flex items-center gap-2">
                          <span>Role:</span>
                          {user.role === 'super_admin' ? (
                            <span className="font-semibold">{user.role}</span>
                          ) : (
                            <Select
                              value={user.role}
                              onValueChange={async (newRole) => {
                                try {
                                  await api.put(`${API}/users/${user.id}/permissions`, { role: newRole });
                                  setUsers(users.map(u => u.id === user.id ? { ...u, role: newRole } : u));
                                  toast.success('User role updated');
                                } catch (error) {
                                  toast.error('Failed to update role');
                                }
                              }}
                            >
                              <SelectTrigger className="w-32 h-6 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                <SelectItem value="user">user</SelectItem>
                                <SelectItem value="admin">admin</SelectItem>
                                <SelectItem value="super_admin">super_admin</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 flex items-center gap-2">
                          <span>Display Name:</span>
                          <Input
                            type="text"
                            placeholder="Enter name..."
                            defaultValue={user.display_name || ''}
                            className="w-40 h-6 text-xs px-2"
                            onClick={(e) => e.stopPropagation()}
                            onBlur={async (e) => {
                              const newName = e.target.value.trim();
                              if (newName !== (user.display_name || '')) {
                                try {
                                  await api.put(`${API}/users/${user.id}/permissions`, { display_name: newName });
                                  setUsers(users.map(u => u.id === user.id ? { ...u, display_name: newName } : u));
                                  toast.success('Display name updated');
                                } catch (error) {
                                  toast.error('Failed to update display name');
                                }
                              }
                            }}
                          />
                        </div>
                        <div className="text-xs text-gray-400 flex items-center gap-2">
                          <span>Team:</span>
                          <Select
                            value={user.team || 'none'}
                            onValueChange={async (newTeam) => {
                              const teamValue = newTeam === 'none' ? '' : newTeam;
                              try {
                                await api.put(`${API}/users/${user.id}/permissions`, { team: teamValue });
                                setUsers(users.map(u => u.id === user.id ? { ...u, team: teamValue } : u));
                                toast.success('Team updated');
                              } catch (error) {
                                toast.error('Failed to update team');
                              }
                            }}
                          >
                            <SelectTrigger className="w-36 h-6 text-xs">
                              <SelectValue placeholder="No team" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="none">No Team</SelectItem>
                              <SelectItem value="warehouse">Warehouse</SelectItem>
                              <SelectItem value="operations">Operations</SelectItem>
                              <SelectItem value="office_sales">Office Sales</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {/* MFA Reset Button */}
                        {realMfaEnabled && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); resetUserMfa(user.id, user.username); }}
                            className="h-6 text-xs border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                          >
                            <Key className="w-3 h-3 mr-1" /> Reset MFA
                          </Button>
                        )}
                        {/* Only protect j.moseley@encon.co.uk from deletion */}
                        {(user.email || '').toLowerCase() !== 'j.moseley@encon.co.uk' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const res = await api.post(`${API}/auth/impersonate/${user.id}`);
                                  if (onImpersonate) {
                                    // Soft in-memory switch — no hard page reload, so a
                                    // brief CORS/preflight hiccup on production's custom
                                    // domain can't wipe the token before the switch lands.
                                    onImpersonate(res.data.access_token, res.data.user);
                                    navigate('/');
                                  } else {
                                    // Fallback (no callback wired) — old hard-reload path
                                    sessionStorage.setItem('_admin_token', localStorage.getItem('token'));
                                    sessionStorage.setItem('_impersonated_as', user.full_name || user.email);
                                    localStorage.setItem('token', res.data.access_token);
                                    window.location.href = '/';
                                  }
                                } catch {
                                  toast.error('Failed to impersonate user');
                                }
                              }}
                              className="h-6 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                            >
                              <LogIn className="w-3 h-3 mr-1" /> Login As
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); deleteUser(user.id, user.username); }}
                              className="h-6 text-xs border-red-300 text-red-600 hover:bg-red-50 ml-auto"
                            >
                              Delete User
                            </Button>
                          </>
                        )}
                        {(user.email || '').toLowerCase() === 'j.moseley@encon.co.uk' && (
                          <span className="text-xs text-gray-400 ml-auto italic">Primary admin (protected)</span>
                        )}
                      </div>
                      
                      {/* Page Permissions Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {/* Account Approval Toggle */}
                        <div className="col-span-2 md:col-span-3 mb-2 p-2 bg-orange-50 rounded border border-orange-200">
                          <div className="flex items-center gap-2">
                            <Switch
                              id={`approved-${user.id}`}
                              checked={user.approved || false}
                              onCheckedChange={() => togglePermission(user.id, 'approved')}
                              disabled={user.role === 'super_admin'}
                            />
                            <Label htmlFor={`approved-${user.id}`} className="text-sm cursor-pointer font-semibold text-orange-700">
                              Account Approved (User can login)
                            </Label>
                          </div>
                        </div>
                        
                        {/* Limited Access UI - personal override */}
                        {user.role !== 'super_admin' && (
                          <div
                            className="col-span-2 md:col-span-3 mb-2 p-3 rounded border"
                            style={{ backgroundColor: 'rgba(238,114,3,0.06)', borderColor: 'rgba(238,114,3,0.35)' }}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="min-w-0">
                                <Label className="text-sm font-semibold" style={{ color: '#ee7203' }}>
                                  Limited Access UI
                                </Label>
                                <p className="text-xs text-gray-600 mt-1">
                                  Inherit follows this user's branch setting (Settings &gt; Branches). On or Off overrides the branch for this user only.
                                </p>
                              </div>
                              <Select
                                value={user.limited_access === true ? 'on' : user.limited_access === false ? 'off' : 'inherit'}
                                onValueChange={async (choice) => {
                                  const next = choice === 'on' ? true : choice === 'off' ? false : null;
                                  const previous = user.limited_access === undefined ? null : user.limited_access;
                                  setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, limited_access: next } : u)));
                                  try {
                                    await api.put(`${API}/users/${user.id}/limited-access`, { limited_access: next });
                                    toast.success('Limited access setting updated');
                                  } catch (error) {
                                    setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, limited_access: previous } : u)));
                                    toast.error(error?.response?.data?.detail || 'Failed to update limited access');
                                  }
                                }}
                              >
                                <SelectTrigger className="w-48 h-8 text-xs" data-testid={`limited-access-select-${user.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  <SelectItem value="inherit">Inherit from branch</SelectItem>
                                  <SelectItem value="on">Limited UI - On</SelectItem>
                                  <SelectItem value="off">Full UI - Off</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}

                        {/* MFA Email Override Section */}
                        <div className="col-span-2 md:col-span-3 mb-2 p-3 bg-blue-50 rounded border border-blue-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Mail className="w-4 h-4 text-blue-600" />
                            <Label className="text-sm font-semibold text-blue-700">
                              MFA Email Address
                            </Label>
                          </div>
                          <p className="text-xs text-blue-600 mb-2">
                            Set an alternate email to receive MFA codes (leave blank to use username)
                          </p>
                          <div className="flex gap-2">
                            <Input
                              type="email"
                              placeholder="Enter email for MFA codes..."
                              value={user.mfa_email || ''}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                setUsers(prev => prev.map(u => 
                                  u.id === user.id ? { ...u, mfa_email: newValue } : u
                                ));
                              }}
                              className="flex-1 h-8 text-sm"
                              data-testid={`mfa-email-${user.id}`}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                              onClick={async () => {
                                try {
                                  await api.put(`${API}/users/${user.id}/mfa-email`, {
                                    mfa_email: user.mfa_email || ''
                                  });
                                  toast.success('MFA email updated');
                                } catch (error) {
                                  console.error('Error updating MFA email:', error);
                                  toast.error('Failed to update MFA email');
                                }
                              }}
                              data-testid={`save-mfa-email-${user.id}`}
                            >
                              Save
                            </Button>
                          </div>
                          {user.mfa_email && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ MFA codes will be sent to: {user.mfa_email}
                            </p>
                          )}
                        </div>

                        {/* MFA Overview Page Access */}
                        {user.role !== 'super_admin' && (
                          <div className="col-span-2 md:col-span-3 mb-2 p-3 bg-blue-50 rounded border border-blue-200">
                            <div className="flex items-center gap-2">
                              <Switch
                                id={`mfa-overview-${user.id}`}
                                checked={user.can_view_mfa_overview || false}
                                onCheckedChange={() => togglePermission(user.id, 'can_view_mfa_overview')}
                                data-testid={`mfa-overview-toggle-${user.id}`}
                              />
                              <Label htmlFor={`mfa-overview-${user.id}`} className="text-sm cursor-pointer font-semibold text-blue-700">
                                MFA Overview Access
                              </Label>
                            </div>
                            <p className="text-xs text-blue-600 mt-1">
                              Lets this user see the read-only MFA overview page under Settings (no edit/reset actions)
                            </p>
                          </div>
                        )}

                        <div className="col-span-2 md:col-span-3 mb-2 p-3 bg-orange-50 rounded border border-orange-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Mail className="w-4 h-4 text-orange-600" />
                            <Label className="text-sm font-semibold text-orange-700">
                              Alias / Trading Email Addresses
                            </Label>
                          </div>
                          <p className="text-xs text-orange-600 mb-2">
                            Comma-separated list of additional sender addresses (e.g. a shared etrade/EDI mailbox
                            configured in Power Automate) that should be attributed to this user for Recent Quotes
                            and order automation, even though it isn't their own login email.
                          </p>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              placeholder="e.g. etrade247E70@nevilllong.co.uk, orders-e70@nevilllong.co.uk"
                              value={user.alias_emails_input ?? (user.alias_emails || []).join(', ')}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                setUsers(prev => prev.map(u =>
                                  u.id === user.id ? { ...u, alias_emails_input: newValue } : u
                                ));
                              }}
                              className="flex-1 h-8 text-sm"
                              data-testid={`alias-emails-${user.id}`}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                              onClick={async () => {
                                const raw = user.alias_emails_input ?? (user.alias_emails || []).join(', ');
                                const alias_emails = raw.split(',').map(s => s.trim()).filter(Boolean);
                                try {
                                  await api.put(`${API}/users/${user.id}/alias-emails`, { alias_emails, alias_emails_override: user.alias_emails_override === true });
                                  setUsers(prev => prev.map(u =>
                                    u.id === user.id ? { ...u, alias_emails, alias_emails_input: undefined } : u
                                  ));
                                  toast.success('Alias emails updated');
                                } catch (error) {
                                  console.error('Error updating alias emails:', error);
                                  toast.error(error?.response?.data?.detail || 'Failed to update alias emails');
                                }
                              }}
                              data-testid={`save-alias-emails-${user.id}`}
                            >
                              Save
                            </Button>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Switch
                              id={`alias-override-${user.id}`}
                              checked={user.alias_emails_override === true}
                              onCheckedChange={async (checked) => {
                                setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, alias_emails_override: checked } : u)));
                                try {
                                  await api.put(`${API}/users/${user.id}/alias-emails`, {
                                    alias_emails: user.alias_emails || [],
                                    alias_emails_override: checked,
                                  });
                                  toast.success(checked
                                    ? 'Alias addresses now take priority over other users\' usernames'
                                    : 'Usernames now take priority over this alias');
                                } catch (error) {
                                  setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, alias_emails_override: !checked } : u)));
                                  console.error('Error updating alias priority:', error);
                                  toast.error(error?.response?.data?.detail || 'Failed to update alias priority');
                                }
                              }}
                              disabled={(user.alias_emails || []).length === 0}
                              data-testid={`alias-override-${user.id}`}
                            />
                            <Label htmlFor={`alias-override-${user.id}`} className="text-xs cursor-pointer text-orange-700 font-medium">
                              Alias takes priority over the matching user's own username
                            </Label>
                          </div>
                          <p className="text-[11px] text-orange-600 mt-1">
                            Off (default): if an alias is also another user's username (e.g. j.moseley@encon.co.uk), quotes and orders from that address go to that user. On: they go to this user instead.
                          </p>
                          {(user.alias_emails || []).length > 0 && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Also matched as: {(user.alias_emails || []).join(', ')}
                            </p>
                          )}
                        </div>

                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`transport-${user.id}`}
                      checked={user.can_access_transport || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_access_transport')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`transport-${user.id}`} className="text-sm cursor-pointer">
                      Transport (View)
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`transport-edit-${user.id}`}
                      checked={user.can_edit_transport || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_edit_transport')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`transport-edit-${user.id}`} className="text-sm cursor-pointer">
                      Transport (Edit)
                    </Label>
                  </div>
                  
                  {/* View Mode Entry Actions Section */}
                  <div className="col-span-2 md:col-span-3 mt-2 pt-2 border-t">
                    <div className="text-xs font-semibold text-gray-600 mb-2">View Mode Entry Actions:</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`view-mark-complete-${user.id}`}
                          checked={user.can_mark_complete || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_mark_complete')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`view-mark-complete-${user.id}`} className="text-xs cursor-pointer">
                          Mark Complete
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`view-mark-failed-${user.id}`}
                          checked={user.can_mark_failed || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_mark_failed')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`view-mark-failed-${user.id}`} className="text-xs cursor-pointer">
                          Mark Failed
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`view-mark-collected-${user.id}`}
                          checked={user.can_mark_collected || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_mark_collected')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`view-mark-collected-${user.id}`} className="text-xs cursor-pointer">
                          Mark Collected
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`view-edit-notes-${user.id}`}
                          checked={user.can_edit_notes || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_edit_notes')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`view-edit-notes-${user.id}`} className="text-xs cursor-pointer">
                          Edit Notes
                        </Label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`reports-${user.id}`}
                      checked={user.can_access_reports || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_access_reports')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`reports-${user.id}`} className="text-sm cursor-pointer">
                      Reports
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`company-car-${user.id}`}
                      checked={user.can_access_company_car || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_access_company_car')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`company-car-${user.id}`} className="text-sm cursor-pointer">
                      Company Car
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`mileage-${user.id}`}
                      checked={user.mileage_tracker_access || false}
                      onCheckedChange={() => togglePermission(user.id, 'mileage_tracker_access')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`mileage-${user.id}`} className="text-sm cursor-pointer">
                      Mileage Tracker
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`settings-${user.id}`}
                      checked={user.can_access_settings || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_access_settings')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`settings-${user.id}`} className="text-sm cursor-pointer">
                      Settings
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`switch-branches-${user.id}`}
                      checked={user.can_switch_branches || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_switch_branches')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`switch-branches-${user.id}`} className="text-sm cursor-pointer text-purple-600 font-medium">
                      Branch Switcher
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`sales-${user.id}`}
                      checked={user.can_access_sales || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_access_sales')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`sales-${user.id}`} className="text-sm cursor-pointer">
                      Sales Dashboard
                    </Label>
                  </div>
                  
                  {/* Sales Dashboard Cards Section */}
                  <div className="col-span-2 md:col-span-3 mt-2 pt-2 border-t">
                    <div className="text-xs font-semibold text-gray-600 mb-2" style={{ color: '#ee7203' }}>
                      Sales Dashboard Cards:
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`sales-projects-${user.id}`}
                          checked={user.sales_dashboard_projects !== false}
                          onCheckedChange={() => togglePermission(user.id, 'sales_dashboard_projects')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`sales-projects-${user.id}`} className="text-sm cursor-pointer">
                          Projects
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`sales-budgets-${user.id}`}
                          checked={user.sales_dashboard_budgets || user.role === 'super_admin' || false}
                          onCheckedChange={() => togglePermission(user.id, 'sales_dashboard_budgets')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`sales-budgets-${user.id}`} className="text-sm cursor-pointer">
                          Budgets {user.role !== 'super_admin' && !user.sales_dashboard_budgets && <span className="text-xs text-gray-400">(Admin)</span>}
                        </Label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`sales-tracker-${user.id}`}
                      checked={user.can_view_sales_tracker || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_view_sales_tracker')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`sales-tracker-${user.id}`} className="text-sm cursor-pointer">
                      Sales Tracker (Reports)
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id={`error-log-${user.id}`}
                      checked={user.can_view_error_log || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_view_error_log')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`error-log-${user.id}`} className="text-sm cursor-pointer">
                      Error Log
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id={`upload-sales-${user.id}`}
                      checked={user.can_upload_sales_tracker || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_upload_sales_tracker')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`upload-sales-${user.id}`} className="text-sm cursor-pointer">
                      Sales Tracker Upload
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id={`global-upload-${user.id}`}
                      checked={user.can_access_global_upload || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_access_global_upload')}
                      disabled={user.role === 'super_admin'}
                    />
                    <Label htmlFor={`global-upload-${user.id}`} className="text-sm cursor-pointer">
                      Global Upload Button
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id={`bi-reports-${user.id}`}
                      checked={user.can_access_bi_reports || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_access_bi_reports')}
                      disabled={user.role === 'super_admin'}
                      data-testid={`toggle-can-access-bi-reports-${user.id}`}
                    />
                    <Label htmlFor={`bi-reports-${user.id}`} className="text-sm cursor-pointer">
                      BI Reports
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id={`cancelled-notif-${user.id}`}
                      checked={user.can_view_cancelled_notif || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_view_cancelled_notif')}
                      disabled={user.role === 'super_admin'}
                      data-testid={`toggle-can-view-cancelled-notif-${user.id}`}
                    />
                    <Label htmlFor={`cancelled-notif-${user.id}`} className="text-sm cursor-pointer">
                      Cancelled Notif (Admin dropdown)
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id={`pricing-${user.id}`}
                      checked={user.can_access_pricing || false}
                      onCheckedChange={() => togglePermission(user.id, 'can_access_pricing')}
                      disabled={user.role === 'super_admin'}
                      data-testid={`toggle-can-access-pricing-${user.id}`}
                    />
                    <Label htmlFor={`pricing-${user.id}`} className="text-sm cursor-pointer">
                      Pricing (Settings)
                    </Label>
                  </div>

                  {/* PI Counts & Stock Management Section */}
                  <div className="col-span-2 md:col-span-3 mt-2 pt-2 border-t">
                    <div className="text-xs font-semibold text-gray-600 mb-2">PI Counts & Stock Management Access:</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`pi-counts-${user.id}`}
                          checked={user.can_access_pi_counts || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_pi_counts')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`pi-counts-${user.id}`} className="text-sm cursor-pointer">
                          PI Counts
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`stock-management-${user.id}`}
                          checked={user.can_access_stock_management || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_stock_management')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`stock-management-${user.id}`} className="text-sm cursor-pointer">
                          Stock Management
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`pi-warehouse-${user.id}`}
                          checked={user.can_view_pi_warehouse || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_view_pi_warehouse')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`pi-warehouse-${user.id}`} className="text-sm cursor-pointer">
                          PI Counts W/H
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`pi-visibility-restricted-${user.id}`}
                          checked={user.pi_counts_visibility_restricted || false}
                          onCheckedChange={() => togglePermission(user.id, 'pi_counts_visibility_restricted')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`pi-visibility-restricted-${user.id}`} className="text-sm cursor-pointer text-amber-600">
                          Visibility Restricted
                        </Label>
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          id={`pi-reports-${user.id}`}
                          checked={user.can_access_pi_reports || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_pi_reports')}
                          disabled={user.role === 'super_admin'}
                          data-testid={`toggle-can-access-pi-reports-${user.id}`}
                        />
                        <Label htmlFor={`pi-reports-${user.id}`} className="text-sm cursor-pointer text-violet-700">
                          PI Reports
                        </Label>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="text-amber-600">Visibility Restricted</span>: Only shows completed counts with summary badges for in-progress/warehouse items. <span className="text-violet-700">PI Reports</span>: enables the Report + Saved buttons on the PI Counts page for date-ranged supplier roll-ups (default OFF).
                    </p>
                  </div>

                  {/* Goods In Section */}
                  <div className="col-span-2 md:col-span-3 mt-2 pt-2 border-t">
                    <div className="text-xs font-semibold text-gray-600 mb-2">Goods In Access:</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`goods-in-${user.id}`}
                          checked={user.can_access_goods_in || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_goods_in')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`goods-in-${user.id}`} className="text-sm cursor-pointer">
                          Goods In
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`goods-in-warehouse-${user.id}`}
                          checked={user.can_access_goods_in_warehouse || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_goods_in_warehouse')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`goods-in-warehouse-${user.id}`} className="text-sm cursor-pointer">
                          Goods In W/H
                        </Label>
                      </div>
                    </div>
                  </div>

                  {/* External Links Section */}
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs font-semibold text-gray-600 mb-2">External Links Access:</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`staffology-${user.id}`}
                          checked={user.can_access_staffology || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_staffology')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`staffology-${user.id}`} className="text-sm cursor-pointer">
                          Staffology
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`crm-${user.id}`}
                          checked={user.can_access_crm || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_crm')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`crm-${user.id}`} className="text-sm cursor-pointer">
                          CRM Portal
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`ai-assistant-${user.id}`}
                          checked={user.can_access_ai_assistant || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_ai_assistant')}
                          disabled={user.role === 'super_admin'}
                          data-testid={`ai-assistant-toggle-${user.id}`}
                        />
                        <Label htmlFor={`ai-assistant-${user.id}`} className="text-sm cursor-pointer">
                          AI Assistant (Terra)
                        </Label>
                      </div>
                    </div>
                  </div>

                  {/* CRM Auto Upload Section */}
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs font-semibold text-gray-600 mb-2">CRM Auto Upload:</div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`crm-auto-upload-${user.id}`}
                        checked={user.crm_auto_upload !== false}
                        onCheckedChange={() => togglePermission(user.id, 'crm_auto_upload')}
                        data-testid={`toggle-crm-auto-upload-${user.id}`}
                      />
                      <Label htmlFor={`crm-auto-upload-${user.id}`} className="text-sm cursor-pointer">
                        Auto-upload this user's parsed quotes to CRM
                      </Label>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      On by default. Turning it off still lets their quotes land on the dashboard, registered to them — only the automatic CRM upload is skipped.
                    </p>
                  </div>

                  {/* M4.5 Access Section */}
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs font-semibold text-gray-600 mb-2">M4.5 Access:</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`m45-${user.id}`}
                          checked={user.can_access_m45 !== false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_m45')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`m45-${user.id}`} className="text-sm cursor-pointer">
                          M4.5 Hub
                        </Label>
                      </div>
                    </div>
                  </div>

                  {/* M4.5 Pills Access Section */}
                  <div className="col-span-2 md:col-span-3 mt-2 pt-2 border-t">
                    <div className="text-xs font-semibold text-gray-600 mb-2" style={{ color: '#ee7203' }}>M4.5 Pills Access:</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`files-${user.id}`}
                          checked={user.can_access_files !== false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_files')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`files-${user.id}`} className="text-sm cursor-pointer">
                          Files
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`quotes-${user.id}`}
                          checked={user.can_access_quotes !== false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_quotes')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`quotes-${user.id}`} className="text-sm cursor-pointer">
                          Quotes
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`proforma-${user.id}`}
                          checked={user.can_access_proforma !== false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_proforma')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`proforma-${user.id}`} className="text-sm cursor-pointer">
                          Pro Forma
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`stock-transfer-${user.id}`}
                          checked={user.can_access_stock_transfer !== false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_stock_transfer')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`stock-transfer-${user.id}`} className="text-sm cursor-pointer">
                          Stock Transfer
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`po-expenses-${user.id}`}
                          checked={user.can_access_po_expenses !== false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_po_expenses')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`po-expenses-${user.id}`} className="text-sm cursor-pointer">
                          P.O. Expenses
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`tools-${user.id}`}
                          checked={user.can_access_tools || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_tools')}
                        />
                        <Label htmlFor={`tools-${user.id}`} className="text-sm cursor-pointer text-orange-600 font-medium">
                          Tools
                        </Label>
                      </div>
                      
                      {/* Tools sub-permissions - only show when Tools is enabled */}
                      {user.can_access_tools && (
                      <div className="ml-6 pl-3 border-l-2 border-orange-200 space-y-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`interview-notes-${user.id}`}
                            checked={user.can_access_interview_notes || false}
                            onCheckedChange={() => togglePermission(user.id, 'can_access_interview_notes')}
                          />
                          <Label htmlFor={`interview-notes-${user.id}`} className="text-sm cursor-pointer text-orange-500">
                            Interview Notes
                          </Label>
                        </div>
                      </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`al-button-${user.id}`}
                          checked={user.can_access_al_button || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_access_al_button')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`al-button-${user.id}`} className="text-sm cursor-pointer text-purple-600 font-medium">
                          A.L. Header Button
                        </Label>
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          id={`crm-manager-${user.id}`}
                          checked={user.is_crm_manager || false}
                          onCheckedChange={() => togglePermission(user.id, 'is_crm_manager')}
                          data-testid={`crm-manager-toggle-${user.id}`}
                        />
                        <Label htmlFor={`crm-manager-${user.id}`} className="text-sm cursor-pointer text-purple-700 font-medium">
                          CRM Manager
                        </Label>
                      </div>

                      {/* Project Tracking Permissions */}
                      <div className="col-span-2 md:col-span-3 mt-2 pt-2 border-t">
                        <div className="text-xs font-semibold mb-2" style={{ color: '#ee7203' }}>Project Tracking:</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              id={`view-project-tracking-${user.id}`}
                              checked={user.can_view_project_tracking || user.role === 'super_admin' || false}
                              onCheckedChange={() => togglePermission(user.id, 'can_view_project_tracking')}
                              disabled={user.role === 'super_admin'}
                              data-testid={`toggle-can-view-project-tracking-${user.id}`}
                            />
                            <Label htmlFor={`view-project-tracking-${user.id}`} className="text-sm cursor-pointer">
                              View Project Tracking
                              <span className="block text-[10px] text-gray-400 font-normal">See page & button in Manage CRM</span>
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              id={`add-project-tracking-${user.id}`}
                              checked={user.can_add_project_tracking_lists || user.role === 'super_admin' || false}
                              onCheckedChange={() => togglePermission(user.id, 'can_add_project_tracking_lists')}
                              disabled={user.role === 'super_admin'}
                              data-testid={`toggle-can-add-project-tracking-${user.id}`}
                            />
                            <Label htmlFor={`add-project-tracking-${user.id}`} className="text-sm cursor-pointer">
                              Add / Manage Lists
                              <span className="block text-[10px] text-gray-400 font-normal">Create lists & add/remove projects</span>
                            </Label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`al-settings-${user.id}`}
                          checked={user.can_manage_annual_leave || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_manage_annual_leave')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`al-settings-${user.id}`} className="text-sm cursor-pointer text-purple-600 font-medium">
                          A.L. Settings
                        </Label>
                      </div>
                    </div>
                  </div>

                      </div>
                
                      {/* Settings Tab Permissions */}
                      {(user.can_access_settings || user.role === 'super_admin') && (
                        <div className="mt-3 pt-3 border-t">
                          <div className="text-xs font-semibold text-gray-600 mb-2">Settings Tab Access:</div>
                          <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`general-settings-${user.id}`}
                          checked={user.can_view_general_settings || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_view_general_settings')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`general-settings-${user.id}`} className="text-xs cursor-pointer">
                          General Tab
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`vehicles-settings-${user.id}`}
                          checked={user.can_view_vehicles_settings || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_view_vehicles_settings')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`vehicles-settings-${user.id}`} className="text-xs cursor-pointer">
                          Vehicles Tab
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`file-manager-${user.id}`}
                          checked={user.can_view_file_manager || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_view_file_manager')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`file-manager-${user.id}`} className="text-xs cursor-pointer">
                          File Manager Tab
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`code-editor-${user.id}`}
                          checked={user.can_view_code_editor || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_view_code_editor')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`code-editor-${user.id}`} className="text-xs cursor-pointer">
                          Code Editor Tab
                        </Label>
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          id={`notif-bar-${user.id}`}
                          checked={user.can_manage_global_notifications || false}
                          onCheckedChange={() => togglePermission(user.id, 'can_manage_global_notifications')}
                          disabled={user.role === 'super_admin'}
                        />
                        <Label htmlFor={`notif-bar-${user.id}`} className="text-xs cursor-pointer text-amber-700 font-medium">
                          Notification Bar
                        </Label>
                      </div>
                    </div>
                  </div>
                      )}
                
                      {/* Password Manager Button */}
                      {user.role !== 'super_admin' && (
                        <div className="mt-3 pt-3 border-t flex justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUserForPassword(user);
                              setShowPasswordManagerModal(true);
                            }}
                            className="gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                            Reset Password
                          </Button>
                        </div>
                      )}
                
                      {user.role === 'super_admin' && (
                        <div className="text-xs text-gray-500 italic pt-2">
                          Super admin has access to all pages by default
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    
      </div>
      )}
      </div>

      {/* Password Manager Modal */}
      <Dialog open={showPasswordManagerModal} onOpenChange={setShowPasswordManagerModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Password Manager - {selectedUserForPassword?.username}</DialogTitle>
            <DialogDescription>
              Set a new password for this user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Reset Password Section */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Set New Password</Label>
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPasswordManagerModal(false);
                setNewPassword('');
                setConfirmPassword('');
                setSelectedUserForPassword(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={resetUserPassword}
              disabled={!newPassword || !confirmPassword}
              style={{ backgroundColor: '#ee7203', color: 'white' }}
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <MfaPasswordUnlock open={showUnlock} onOpenChange={setShowUnlock} onUnlocked={handleUnlocked} />

      {/* MFA Users Modal */}
      <Dialog open={showMfaUsersModal} onOpenChange={setShowMfaUsersModal}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mfaModalFilter === 'enabled' ? (
                <>
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  Users with MFA Enabled
                </>
              ) : (
                <>
                  <ShieldAlert className="w-5 h-5 text-yellow-600" />
                  Users without MFA
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {mfaModalFilter === 'enabled' 
                ? 'Click "Reset MFA" to remove a user\'s MFA setup. They will need to set it up again on next login.'
                : 'These users have not set up MFA yet. They will be prompted on their next login if MFA is required globally.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Filter Tabs */}
          <div className="flex gap-2 border-b pb-3">
            <button
              onClick={() => setMfaModalFilter('enabled')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                mfaModalFilter === 'enabled'
                  ? 'bg-green-100 text-green-700 border-2 border-green-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <ShieldCheck className="w-4 h-4 inline mr-2" />
              MFA Enabled ({mfaSettings.mfa_enabled_count})
            </button>
            <button
              onClick={() => setMfaModalFilter('disabled')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                mfaModalFilter === 'disabled'
                  ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <ShieldAlert className="w-4 h-4 inline mr-2" />
              Without MFA ({mfaSettings.total_users - mfaSettings.mfa_enabled_count})
            </button>
          </div>
          
          {/* Users List */}
          <div className="flex-1 overflow-y-auto space-y-2 py-2">
            {mfaSettings.users
              .filter(u => mfaModalFilter === 'enabled' ? u.effective_mfa_enabled : !u.effective_mfa_enabled)
              .map(user => (
                <div 
                  key={user.id} 
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    mfaModalFilter === 'enabled' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      mfaModalFilter === 'enabled' ? 'bg-green-200' : 'bg-yellow-200'
                    }`}>
                      {mfaModalFilter === 'enabled' ? (
                        (!mfaSettings.authenticator_enabled || user.mfa_method === 'email') ? (
                          <Mail className="w-5 h-5 text-green-700" />
                        ) : (
                          <Smartphone className="w-5 h-5 text-green-700" />
                        )
                      ) : (
                        <ShieldAlert className="w-5 h-5 text-yellow-700" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {user.full_name || user.email}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      {mfaModalFilter === 'enabled' && (
                        <div className="text-xs text-green-600 mt-0.5">
                          {(!mfaSettings.authenticator_enabled || user.mfa_method === 'email') ? 'Email Code' : 'Authenticator App'}
                          {user.pending_first_login ? ' · not logged in yet' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  {mfaModalFilter === 'enabled' && user.mfa_enabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resetUserMfa(user.id, user.full_name || user.email)}
                      className="border-red-300 text-red-600 hover:bg-red-50 gap-2"
                    >
                      <Key className="w-4 h-4" />
                      Reset MFA
                    </Button>
                  )}
                </div>
              ))}
            
            {mfaSettings.users.filter(u => mfaModalFilter === 'enabled' ? u.effective_mfa_enabled : !u.effective_mfa_enabled).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {mfaModalFilter === 'enabled' 
                  ? 'No users have MFA enabled yet.'
                  : 'All users have MFA enabled!'
                }
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowMfaUsersModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionsManager;
